<script setup lang="ts">
import { useHead, useFetch, useRoute, useRuntimeConfig } from '#app'
import { useAuthStore } from '../stores/authStore'
import type { CmsSettings } from '../types/cmsTypes'
import { onMounted } from 'vue'

const route = useRoute()
const config = useRuntimeConfig()
const { data: settings, refresh: refreshSettings } = await useFetch<CmsSettings>(`${config.public.apiBase}/api/get-cms-settings`, {
  default: () => ({} as CmsSettings),
  transform: response => (response ?? {}) as CmsSettings,
  getCachedData: (key, nuxtApp) =>
    nuxtApp.isHydrating ? nuxtApp.payload.data[key] : undefined
})
const { data: pageSettings, refresh: refreshPageSettings } = await useFetch<CmsSettings>(`${config.public.apiBase}/api/get-page-settings`, {
  query: { slug: route.path },
  default: () => ({} as CmsSettings),
  transform: response => (response ?? {}) as CmsSettings,
  getCachedData: (key, nuxtApp) =>
    nuxtApp.isHydrating ? nuxtApp.payload.data[key] : undefined
})

const authStore = useAuthStore()
onMounted(async () => {
  if (authStore.authorized) {
    await Promise.all([refreshSettings(), refreshPageSettings()])
  }
})

const currentUrl = () => `https://ostoja-seniora.pl${route.path}`

const headGtmCode = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${settings.value.googleTagId}');`
const bodyGtmNoscript = `<!-- Google Tag Manager (noscript) --><iframe src="https://www.googletagmanager.com/ns.html?id=${settings.value.googleTagId}" height="0" width="0" style="display:none;visibility:hidden"></iframe><!-- End Google Tag Manager (noscript) -->`
const gtagSrc = () => `https://www.googletagmanager.com/gtag/js?id=${settings.value.googleAnalyticsId}`
const gtagInline = `window.dataLayer = window.dataLayer || [];function gtag(){dataLayer.push(arguments);}gtag('js', new Date());gtag('config', '${settings.value.googleAnalyticsId}');`
const googleClarity = `(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);})(window, document, "clarity", "script", "${settings.value.microsoftClarity}");`


const stripScriptTags = (code: string) =>
  code.replace(/<script[^>]*>/gi, '').replace(/<\/script>/gi, '')

useHead({
  htmlAttrs: { lang: 'pl' },
  title: () => pageSettings.value?.metaTitle || settings.value?.metaTitle || '',
  link: [
    { rel: 'icon', type: 'image/x-icon', href: () => pageSettings.value?.faviconIco || settings.value?.faviconIco || '' },
    { rel: 'icon', type: 'image/png', sizes: '16x16', href: () => pageSettings.value?.favico16 || settings.value?.favico16 || '' },
    { rel: 'icon', type: 'image/png', sizes: '32x32', href: () => pageSettings.value?.favico32 || settings.value?.favico32 || '' },
    { rel: 'icon', type: 'image/png', sizes: '48x48', href: () => pageSettings.value?.favico48 || settings.value?.favico48 || '' },
    { rel: 'icon', type: 'image/png', sizes: '64x64', href: () => pageSettings.value?.favico64 || settings.value?.favico64 || '' },
    { rel: 'apple-touch-icon', sizes: '180x180', href: () => pageSettings.value?.appleTouchIcon || pageSettings.value?.favico180 || settings.value?.appleTouchIcon || settings.value?.favico180 || '' },
    { rel: 'icon', type: 'image/png', sizes: '192x192', href: () => pageSettings.value?.favico192 || settings.value?.favico192 || '' },
    { rel: 'icon', type: 'image/png', sizes: '512x512', href: () => pageSettings.value?.favico512 || settings.value?.favico512 || '' },
    { rel: 'canonical', href: () => pageSettings.value?.canonicalUrl || currentUrl() }
  ],
  meta: [
    { name: 'viewport', content: 'width=device-width, initial-scale=1' },
    { name: 'description', content: () => pageSettings.value?.metaDescription || settings.value?.metaDescription || '' },
    { name: 'author', content: 'Reequest' },
    { property: 'og:title', content: () => pageSettings.value?.metaTitle || settings.value?.metaTitle || '' },
    { property: 'og:description', content: () => pageSettings.value?.metaDescription || settings.value?.metaDescription || '' },
    { property: 'og:url', content: () => pageSettings.value?.canonicalUrl || currentUrl() },
    { property: 'og:type', content: 'website' },
    { property: 'og:site_name', content: () => settings.value?.siteName || '' },
    { property: 'og:locale', content: 'pl_PL' },
    { property: 'og:image', content: () => pageSettings.value?.ogImage || settings.value?.ogImage || '' },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: () => pageSettings.value?.metaTitle || settings.value?.metaTitle || '' },
    { name: 'twitter:description', content: () => pageSettings.value?.metaDescription || settings.value?.metaDescription || '' },
    { name: 'twitter:image', content: () => pageSettings.value?.ogImage || settings.value?.ogImage || '' },
    { name: 'robots', content: 'index, follow' },
    {
      name: 'keywords',
      content: 'dom opieki dla seniorów łódź, apartamenty dla seniorów, sprzedaż mieszkań dla seniorów, wynajem mieszkania dla seniora, prywatny dom opieki łódź'
    }
  ],
  script: [
    {
      'src': () => settings.value?.cookiebotId ? 'https://consent.cookiebot.com/uc.js' : '',
      'id': 'Cookiebot',
      'data-cbid': () => settings.value?.cookiebotId || '',
      'data-blockingmode': 'none',
      'type': 'text/javascript',
      'defer': true
    },
    { innerHTML: () => stripScriptTags(settings.value?.customHeadCode || '') },
    { innerHTML: () => stripScriptTags(pageSettings.value?.customHeadCode || '') },
    { innerHTML: () => stripScriptTags(settings.value?.customBodyCode || ''), tagPosition: 'bodyClose' },
    { innerHTML: () => stripScriptTags(pageSettings.value?.customBodyCode || ''), tagPosition: 'bodyClose' },

    { innerHTML: () => settings.value?.googleTagId ? headGtmCode : '' },
    { src: () => settings.value?.googleAnalyticsId ? gtagSrc() : undefined, async: true },
    { innerHTML: () => settings.value?.googleAnalyticsId ? gtagInline : '', tagPosition: 'bodyClose' },
    { innerHTML: () => settings.value?.microsoftClarity ? googleClarity : '' }
  ],
  noscript: [
    { innerHTML: () => settings.value?.googleTagId ? bodyGtmNoscript : '', tagPosition: 'bodyOpen' }
  ]
})
</script>

<template>
  <div />
</template>
